import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  ASSISTANT_SESSION_STORAGE_KEY,
  AssistantAttachment,
  AssistantMessage,
  AssistantState,
} from "../interfaces/assistant.interface";

const readStoredSessionId = (): string | null => {
  try {
    return sessionStorage.getItem(ASSISTANT_SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeStoredSessionId = (sessionId: string | null): void => {
  try {
    if (sessionId) {
      sessionStorage.setItem(ASSISTANT_SESSION_STORAGE_KEY, sessionId);
    } else {
      sessionStorage.removeItem(ASSISTANT_SESSION_STORAGE_KEY);
    }
  } catch {
    /* private browsing — the conversation just won't survive a reload */
  }
};

const makeId = (): string =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const initialState: AssistantState = {
  isOpen: false,
  isRestoring: false,
  isEnabled: null,
  sessionId: readStoredSessionId(),
  messages: [],
  isStreaming: false,
  toolStatus: null,
  unreadCount: 0,
  lastQuestion: null,
};

/** The assistant message currently being streamed into, if any. */
const streamingMessage = (state: AssistantState): AssistantMessage | undefined => {
  const last = state.messages[state.messages.length - 1];
  return last?.role === "assistant" ? last : undefined;
};

const assistantSlice = createSlice({
  name: "assistant",
  initialState,
  reducers: {
    setEnabled(state, action: PayloadAction<boolean>) {
      state.isEnabled = action.payload;
    },
    openAssistant(state) {
      state.isOpen = true;
      state.unreadCount = 0;
    },
    closeAssistant(state) {
      state.isOpen = false;
    },
    toggleAssistant(state) {
      state.isOpen = !state.isOpen;
      if (state.isOpen) state.unreadCount = 0;
    },
    setSessionId(state, action: PayloadAction<string>) {
      state.sessionId = action.payload;
      writeStoredSessionId(action.payload);
    },
    setRestoring(state, action: PayloadAction<boolean>) {
      state.isRestoring = action.payload;
    },
    /** Replays a stored conversation fetched from the server after a reload. */
    restoreMessages(
      state,
      action: PayloadAction<{ role: "user" | "assistant"; text: string }[]>,
    ) {
      state.isRestoring = false;
      // A conversation started in this tab always wins over the replayed copy.
      if (state.messages.length) return;
      state.messages = action.payload.map((entry) => ({
        id: makeId(),
        role: entry.role,
        text: entry.text,
        attachments: [],
        createdAt: Date.now(),
      }));
    },
    appendUserMessage(state, action: PayloadAction<string>) {
      state.lastQuestion = action.payload;
      state.messages.push({
        id: makeId(),
        role: "user",
        text: action.payload,
        attachments: [],
        createdAt: Date.now(),
      });
    },
    /** Opens an empty assistant bubble that stream deltas flow into. */
    startAssistantMessage(state) {
      state.isStreaming = true;
      state.toolStatus = null;
      state.messages.push({
        id: makeId(),
        role: "assistant",
        text: "",
        attachments: [],
        createdAt: Date.now(),
      });
    },
    appendDelta(state, action: PayloadAction<string>) {
      const message = streamingMessage(state);
      if (message) message.text += action.payload;
      state.toolStatus = null;
    },
    setToolStatus(state, action: PayloadAction<string>) {
      state.toolStatus = action.payload;
    },
    addAttachment(state, action: PayloadAction<AssistantAttachment>) {
      const message = streamingMessage(state);
      if (message) message.attachments.push(action.payload);
    },
    finishAssistantMessage(state) {
      state.isStreaming = false;
      state.toolStatus = null;

      const message = streamingMessage(state);
      // A turn that produced only attachments still needs words in the bubble.
      if (message && !message.text.trim() && !message.attachments.length) {
        state.messages.pop();
      }
      if (!state.isOpen) state.unreadCount += 1;
    },
    failAssistantMessage(state, action: PayloadAction<string>) {
      state.isStreaming = false;
      state.toolStatus = null;

      const message = streamingMessage(state);
      if (message && !message.text.trim() && !message.attachments.length) {
        message.text = action.payload;
        message.isError = true;
        return;
      }
      state.messages.push({
        id: makeId(),
        role: "assistant",
        text: action.payload,
        attachments: [],
        isError: true,
        createdAt: Date.now(),
      });
    },
    /** Drops the failed exchange so a retry does not duplicate it. */
    removeLastExchange(state) {
      const last = state.messages[state.messages.length - 1];
      if (last?.role === "assistant") state.messages.pop();
      const prior = state.messages[state.messages.length - 1];
      if (prior?.role === "user") state.messages.pop();
    },
    clearConversation(state) {
      state.messages = [];
      state.lastQuestion = null;
      state.sessionId = null;
      state.isStreaming = false;
      state.toolStatus = null;
      state.unreadCount = 0;
      writeStoredSessionId(null);
    },
  },
});

export const {
  setEnabled,
  setRestoring,
  restoreMessages,
  removeLastExchange,
  openAssistant,
  closeAssistant,
  toggleAssistant,
  setSessionId,
  appendUserMessage,
  startAssistantMessage,
  appendDelta,
  setToolStatus,
  addAttachment,
  finishAssistantMessage,
  failAssistantMessage,
  clearConversation,
} = assistantSlice.actions;

export default assistantSlice.reducer;
